// ============================================
// Spend Trend Chart Component
// Facebook Ads spend by Business Unit (ECOM vs CONST)
// ============================================

import { LineChart } from '../../../components/charts/LineChart';
import { ChartSkeleton } from '../../../components/layout/LoadingState';
import { formatCurrency } from '../../../utils/formatters';
import type { SpendTrendData } from '../../../types/api';

// Semantic colors from dashboard-design skill
const colors = {
  ecom: '#3B82F6',  // Blue - E-Commerce
  const: '#10B981', // Green - Construction
};

interface SpendTrendChartProps {
  data?: SpendTrendData;
  isLoading: boolean;
}

export function SpendTrendChart({ data, isLoading }: SpendTrendChartProps) {
  if (isLoading) {
    return <ChartSkeleton height={400} />;
  }

  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 h-[400px] flex items-center justify-center">
        <p className="text-gray-500">Không có dữ liệu chi tiêu</p>
      </div>
    );
  }

  // Extract totals - backend returns ecom/const, not facebook/google
  const totals = data.totals as { ecom?: number; const?: number; total?: number };
  const ecomTotal = totals.ecom || 0;
  const constTotal = totals.const || 0;
  const totalSpend = totals.total || ecomTotal + constTotal;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 shadow-sm">
      {/* Header with Legend */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Chi Tiêu Theo Business Unit</h3>
          <p className="text-xs sm:text-sm text-gray-500">Xu hướng chi tiêu Facebook Ads hàng ngày</p>
        </div>
        <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div
              className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full"
              style={{ backgroundColor: colors.ecom }}
            ></div>
            <span className="text-gray-600">
              E-Com: <span className="font-medium">{formatCurrency(ecomTotal)}</span>
            </span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div
              className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full"
              style={{ backgroundColor: colors.const }}
            ></div>
            <span className="text-gray-600">
              Const: <span className="font-medium">{formatCurrency(constTotal)}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Chart - Line with Area for Trend visualization */}
      <LineChart
        data={data.data}
        lines={[
          {
            dataKey: 'ecom',
            name: 'E-Commerce',
            color: colors.ecom,
            type: 'area',
          },
          {
            dataKey: 'const',
            name: 'Construction',
            color: colors.const,
            type: 'area',
          },
        ]}
        height={280}
        formatYAxis={(value) => formatCurrency(value)}
        formatTooltip={(value) => formatCurrency(value)}
      />

      {/* Summary Footer */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
          <div>
            <p className="text-[10px] sm:text-xs text-gray-500 uppercase">E-Commerce</p>
            <p className="text-sm sm:text-lg font-semibold text-gray-900">
              {formatCurrency(ecomTotal)}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-400">
              {totalSpend > 0 ? ((ecomTotal / totalSpend) * 100).toFixed(1) : 0}%
            </p>
          </div>
          <div>
            <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Construction</p>
            <p className="text-sm sm:text-lg font-semibold text-gray-900">
              {formatCurrency(constTotal)}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-400">
              {totalSpend > 0 ? ((constTotal / totalSpend) * 100).toFixed(1) : 0}%
            </p>
          </div>
          <div className="border-l border-gray-200 pl-2 sm:pl-0">
            <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Tổng Chi Tiêu</p>
            <p className="text-base sm:text-xl font-bold text-gray-900">
              {formatCurrency(totalSpend)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
